import React from 'react';
import { Input } from 'reactstrap';
import { ColumnDef, Row } from '@tanstack/react-table';
import classNames from 'classnames';

import ClickableIcon from '~components/ClickableIcon';
import { faCopy, faPencil, faTrash } from '~components/icon';

import CellContent from './CellContent';
import TableEditActions from './TableEditActions';
import { TableViewRow } from './types';

interface ComponentProps {
  row: Row<any>;
  selected: boolean;
  isEditing: boolean;
  editRow?: TableViewRow | null;
  onCellChange?: (row: TableViewRow, column: ColumnDef<any>) => void;
  rowActions: {
    canSelect?: boolean;
    canEdit?: boolean;
    canCopy?: boolean;
    canDelete?: boolean;
    onSelectRow?: (row: TableViewRow | undefined) => void;
    onStartEdit?: (row: TableViewRow) => void;
    onCancelEdit?: (row: TableViewRow) => void;
    onSave?: () => void;
    onCopy?: (row: TableViewRow) => void;
    onDelete?: (row: TableViewRow) => void;
  };
  validateRow: (row: TableViewRow) => boolean;
  validationResults?: Record<string, { error: boolean; message?: string }>;
}

const TableRow: React.FC<ComponentProps> = React.memo(
  ({ row, selected, isEditing, editRow, onCellChange, rowActions, validationResults = {} }) => {
    const { canSelect, canEdit, canCopy, canDelete, onSelectRow, onStartEdit, onCopy, onDelete, onCancelEdit, onSave } =
      rowActions;

    const { original } = row;
    const rowId = row.id;

    const hasAnyError = Object.values(validationResults).some((val) => val?.error);

    const rowClass = classNames('align-middle on-hover', {
      'table-info': selected,
      'table-warning': isEditing && !hasAnyError,
      'table-danger': isEditing && hasAnyError,
    });

    const handleChange = (column: any, newValue: any) => {
      const columnId = column.id;
      const previous = (editRow ?? original)?.[columnId];
      if (previous === newValue) return;

      const updated = { ...(editRow ?? original), [columnId]: newValue };

      onCellChange?.(updated, column);

      if (!editRow || JSON.stringify(updated) !== JSON.stringify(editRow)) {
        rowActions.onStartEdit?.(updated);
      }
    };

    return (
      <tr key={rowId} data-id={rowId} className={rowClass}>
        {canSelect && (
          <td>
            <Input
              type="checkbox"
              checked={selected}
              onChange={() => onSelectRow?.(original)}
              className="checkbox-custom form-check-input"
            />
          </td>
        )}

        {row.getVisibleCells().map((cell) => {
          const { column } = cell;
          const columnId = column.id;
          const meta = column.columnDef.meta ?? {};

          const value = isEditing ? (editRow?.[columnId] ?? original?.[columnId]) : original?.[columnId];
          const validation = validationResults[columnId];

          let styleCell = meta.style?.baseCell as string | ((row: any) => string);
          if (!styleCell) styleCell = meta.style?.cell as string | ((row: any) => string);
          if (typeof styleCell === 'function') {
            styleCell = styleCell(original);
          }

          return (
            <td
              key={cell.id}
              className={classNames('base-cell', styleCell)}
              style={{ width: meta?.style?.width, minWidth: meta?.style?.minWidth }}
              title={isEditing && validation?.message ? validation.message : undefined}
            >
              <CellContent
                cell={cell}
                isEditing={isEditing}
                value={value}
                onChange={(val: any) => handleChange(column, val)}
                row={original}
                isValid={!validation?.error}
              />
            </td>
          );
        })}

        {(canEdit || canCopy || canDelete) && (
          <td className="actions-cell">
            <>
              {isEditing ? (
                <TableEditActions isEditing rowId={rowId} onSave={onSave} onCancel={onCancelEdit} />
              ) : (
                !editRow && (
                  <div className="actions-container">
                    {canEdit && (
                      <ClickableIcon
                        id={`${rowId}-edit`}
                        icon={faPencil}
                        size="small"
                        color="secondary"
                        onClick={() => onStartEdit?.(original)}
                        toolTip="Edit"
                      />
                    )}
                    {canCopy && (
                      <ClickableIcon
                        id={`${rowId}-copy`}
                        icon={faCopy}
                        size="small"
                        color="success"
                        onClick={() => onCopy?.(original)}
                        toolTip="Copy"
                      />
                    )}
                    {canDelete && (
                      <ClickableIcon
                        id={`${rowId}-remove`}
                        icon={faTrash}
                        className="me-2"
                        size="small"
                        color="danger"
                        onClick={() => onDelete?.(original)}
                        toolTip="Delete Row"
                      />
                    )}
                  </div>
                )
              )}
            </>
          </td>
        )}
      </tr>
    );
  },
);

TableRow.displayName = 'TableRow';

export default TableRow;
